import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  FileText, Search, Globe, Calendar, Tag, Loader2, CheckCircle,
  BookOpen, ChevronRight, Check, Clock, Plus, Pencil, Trash2, Send
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const CATEGORIES = [
  { value: 'General', label: 'General' },
  { value: 'HR & Employment', label: 'HR & Employment' },
  { value: 'Health & Safety', label: 'Health & Safety' },
  { value: 'IT & Security', label: 'IT & Security' },
  { value: 'Finance', label: 'Finance' },
  { value: 'Code of Conduct', label: 'Code of Conduct' },
  { value: 'Leave & Attendance', label: 'Leave & Attendance' },
  { value: 'Other', label: 'Other' },
];

const CATEGORY_ICONS = {
  'General': FileText,
  'HR & Employment': BookOpen,
  'Health & Safety': CheckCircle,
  'IT & Security': FileText,
  'Finance': FileText,
  'Code of Conduct': BookOpen,
  'Leave & Attendance': Calendar,
  'Other': FileText,
};

const CATEGORY_COLORS = {
  'General': 'bg-blue-100 text-blue-700',
  'HR & Employment': 'bg-purple-100 text-purple-700',
  'Health & Safety': 'bg-red-100 text-red-700',
  'IT & Security': 'bg-indigo-100 text-indigo-700',
  'Finance': 'bg-green-100 text-green-700',
  'Code of Conduct': 'bg-yellow-100 text-yellow-700',
  'Leave & Attendance': 'bg-orange-100 text-orange-700',
  'Other': 'bg-gray-100 text-gray-700',
};

const CompanyPolicies = () => {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [acknowledgments, setAcknowledgments] = useState({});
  const [acknowledging, setAcknowledging] = useState(false);

  // Admin state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ title: '', content: '', summary: '', category: 'General', requires_acknowledgment: false, tags: '' });

  const { getAuthHeaders, currentOffice, isOfficeAdmin, activeView } = useAuth();
  const isAdmin = isOfficeAdmin() && activeView !== 'employee';
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchPolicies();
  }, [currentOffice]);

  const fetchPolicies = async () => {
    try {
      const headers = getAuthHeaders();
      if (currentOffice?.id) {
        headers['X-Office-ID'] = currentOffice.id;
      }

      // Office admins fetch all (incl drafts), employees only published
      const endpoint = isAdmin ? `${API_URL}/api/policies/office/manage` : `${API_URL}/api/policies/`;
      const response = await fetch(endpoint, { headers });
      if (response.ok) {
        const data = await response.json();
        setPolicies(data);
        
        data.forEach(policy => {
          if (policy.requires_acknowledgment) {
            fetchAcknowledgmentStatus(policy.id);
          }
        });
      }
    } catch (error) {
      console.error('Error fetching policies:', error);
    } finally {
      setLoading(false);
    }
  };

  // Admin functions
  const openCreateModal = () => {
    setEditingPolicy(null);
    setFormData({ title: '', content: '', summary: '', category: 'General', requires_acknowledgment: false, tags: '' });
    setShowCreateModal(true);
  };

  const openEditModal = (policy) => {
    setEditingPolicy(policy);
    setFormData({
      title: policy.title,
      content: policy.content || '',
      summary: policy.summary || '',
      category: policy.category || 'General',
      requires_acknowledgment: policy.requires_acknowledgment || false,
      tags: (policy.tags || []).join(', '),
    });
    setShowCreateModal(true);
  };

  const handleSavePolicy = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error('Title and content are required');
      return;
    }
    setSaving(true);
    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      if (currentOffice?.id) headers['X-Office-ID'] = currentOffice.id;
      
      const body = {
        title: formData.title,
        content: formData.content,
        summary: formData.summary || null,
        category: formData.category,
        requires_acknowledgment: formData.requires_acknowledgment,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      };

      const url = editingPolicy
        ? `${API_URL}/api/policies/office/${editingPolicy.id}`
        : `${API_URL}/api/policies/office`;
      const method = editingPolicy ? 'PUT' : 'POST';

      const res = await fetch(url, { method, headers, body: JSON.stringify(body) });
      if (res.ok) {
        toast.success(editingPolicy ? 'Policy updated' : 'Policy created');
        setShowCreateModal(false);
        fetchPolicies();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save policy');
      }
    } catch (error) {
      toast.error('Failed to save policy');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePolicy = async (policyId) => {
    if (!window.confirm('Are you sure you want to delete this policy?')) return;
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API_URL}/api/policies/office/${policyId}`, { method: 'DELETE', headers });
      if (res.ok) {
        toast.success('Policy deleted');
        fetchPolicies();
      } else {
        toast.error('Failed to delete policy');
      }
    } catch (error) {
      toast.error('Failed to delete policy');
    }
  };

  const handlePublishPolicy = async (policyId) => {
    try {
      const headers = getAuthHeaders();
      const res = await fetch(`${API_URL}/api/policies/office/${policyId}/publish`, { method: 'POST', headers });
      if (res.ok) {
        toast.success('Policy published');
        fetchPolicies();
      } else {
        toast.error('Failed to publish policy');
      }
    } catch (error) {
      toast.error('Failed to publish policy');
    }
  };

  const fetchAcknowledgmentStatus = async (policyId) => {
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_URL}/api/policies/${policyId}/acknowledgment-status`, { headers });
      if (response.ok) {
        const data = await response.json();
        setAcknowledgments(prev => ({ ...prev, [policyId]: data }));
      }
    } catch (error) {
      console.error('Error fetching acknowledgment status:', error);
    }
  };

  const handleAcknowledge = async (policyId) => {
    setAcknowledging(true);
    try {
      const headers = getAuthHeaders();
      const response = await fetch(`${API_URL}/api/policies/${policyId}/acknowledge`, {
        method: 'POST',
        headers,
      });

      if (response.ok) {
        const data = await response.json();
        setAcknowledgments(prev => ({ 
          ...prev, 
          [policyId]: { acknowledged: true, acknowledged_at: data.acknowledged_at }
        }));
      }
    } catch (error) {
      console.error('Error acknowledging policy:', error);
    } finally {
      setAcknowledging(false);
    }
  };

  const openPolicyModal = (policy) => {
    setSelectedPolicy(policy);
    setShowPolicyModal(true);
  };

  // Filter policies
  const filteredPolicies = policies.filter(policy => {
    if (searchQuery && !policy.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (selectedCategory !== 'all' && policy.category !== selectedCategory) return false;
    return true;
  });

  // Group policies by category
  const policiesByCategory = filteredPolicies.reduce((acc, policy) => {
    const cat = policy.category || 'Other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(policy);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Company Policies</h1>
          <p className="text-gray-600">{isAdmin ? 'Create and manage company policies' : 'Read and acknowledge company policies applicable to your region'}</p>
        </div>
        {isAdmin && (
          <Button onClick={openCreateModal} className="bg-[#0F2E6E] hover:bg-[#0a1f4d]" data-testid="create-policy-btn">
            <Plus className="h-4 w-4 mr-2" /> Create Policy
          </Button>
        )}
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search policies..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-policies-input"
                />
              </div>
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-[180px]" data-testid="filter-category-select">
                <Tag className="h-4 w-4 mr-2" />
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {CATEGORIES.map(c => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Policies</p>
                <p className="text-2xl font-bold">{policies.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Clock className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Pending Acknowledgment</p>
                <p className="text-2xl font-bold">
                  {policies.filter(p => p.requires_acknowledgment && !acknowledgments[p.id]?.acknowledged).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Acknowledged</p>
                <p className="text-2xl font-bold">
                  {Object.values(acknowledgments).filter(a => a.acknowledged).length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Policies List */}
      {filteredPolicies.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No policies found</h3>
            <p className="text-gray-500 mt-1">
              {searchQuery || selectedCategory !== 'all' 
                ? 'Try adjusting your search or filters' 
                : 'There are no policies published for your region yet'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(policiesByCategory).map(([category, categoryPolicies]) => {
            const IconComponent = CATEGORY_ICONS[category] || FileText;
            return (
              <div key={category}>
                <div className="flex items-center gap-2 mb-3">
                  <div className={`p-1.5 rounded ${CATEGORY_COLORS[category] || 'bg-gray-100'}`}>
                    <IconComponent className="h-4 w-4" />
                  </div>
                  <h2 className="font-semibold text-gray-900">{category}</h2>
                  <Badge variant="outline">{categoryPolicies.length}</Badge>
                </div>
                <div className="grid gap-3">
                  {categoryPolicies.map(policy => {
                    const ack = acknowledgments[policy.id];
                    const needsAck = policy.requires_acknowledgment && !ack?.acknowledged;
                    
                    return (
                      <Card 
                        key={policy.id} 
                        className={`hover:shadow-md transition-shadow cursor-pointer ${needsAck ? 'border-orange-200 bg-orange-50/30' : ''}`}
                        onClick={() => openPolicyModal(policy)}
                        data-testid={`policy-card-${policy.id}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <h3 className="font-medium">{policy.title}</h3>
                                {policy.country !== 'ALL' && (
                                  <Badge variant="outline" className="text-xs">
                                    <Globe className="h-3 w-3 mr-1" />
                                    {policy.country_name || policy.country}
                                  </Badge>
                                )}
                                {policy.country === 'ALL' && (
                                  <Badge variant="outline" className="text-xs bg-blue-50">
                                    <Globe className="h-3 w-3 mr-1" />
                                    Global
                                  </Badge>
                                )}
                                {needsAck && (
                                  <Badge className="bg-orange-100 text-orange-700 text-xs">
                                    <Clock className="h-3 w-3 mr-1" />
                                    Pending
                                  </Badge>
                                )}
                                {ack?.acknowledged && (
                                  <Badge className="bg-green-100 text-green-700 text-xs">
                                    <Check className="h-3 w-3 mr-1" />
                                    Acknowledged
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-gray-500 mt-1">
                                {policy.summary || `${policy.content.substring(0, 100)}...`}
                              </p>
                              <div className="flex gap-4 mt-2 text-xs text-gray-400">
                                <span>Version {policy.version}</span>
                                {policy.effective_date && (
                                  <span>Effective: {new Date(policy.effective_date).toLocaleDateString()}</span>
                                )}
                                {policy.published_at && <span>Published: {new Date(policy.published_at).toLocaleDateString()}</span>}
                                {isAdmin && policy.status === 'draft' && (
                                  <Badge className="bg-yellow-100 text-yellow-700 text-xs">Draft</Badge>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {isAdmin && (
                                <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                                  {policy.status === 'draft' && (
                                    <Button size="sm" variant="ghost" className="h-8 text-green-600 hover:text-green-700 hover:bg-green-50" onClick={() => handlePublishPolicy(policy.id)} data-testid={`publish-policy-${policy.id}`}>
                                      <Send className="h-3.5 w-3.5" />
                                    </Button>
                                  )}
                                  <Button size="sm" variant="ghost" className="h-8 text-gray-500 hover:text-[#0F2E6E] hover:bg-blue-50" onClick={() => openEditModal(policy)} data-testid={`edit-policy-${policy.id}`}>
                                    <Pencil className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button size="sm" variant="ghost" className="h-8 text-gray-500 hover:text-red-600 hover:bg-red-50" onClick={() => handleDeletePolicy(policy.id)} data-testid={`delete-policy-${policy.id}`}>
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              )}
                              <ChevronRight className="h-5 w-5 text-gray-400" />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Policy Detail Modal */}
      <Dialog open={showPolicyModal} onOpenChange={setShowPolicyModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="policy-detail-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {selectedPolicy?.title}
            </DialogTitle>
          </DialogHeader>

          {selectedPolicy && (
            <div className="space-y-4">
              {/* Meta info */}
              <div className="flex flex-wrap gap-3 pb-4 border-b">
                <Badge className={CATEGORY_COLORS[selectedPolicy.category] || 'bg-gray-100'}>
                  {selectedPolicy.category}
                </Badge>
                <Badge variant="outline">
                  <Globe className="h-3 w-3 mr-1" />
                  {selectedPolicy.country === 'ALL' ? 'Global Policy' : selectedPolicy.country_name}
                </Badge>
                <Badge variant="outline">Version {selectedPolicy.version}</Badge>
                {selectedPolicy.effective_date && (
                  <Badge variant="outline">
                    <Calendar className="h-3 w-3 mr-1" />
                    Effective: {new Date(selectedPolicy.effective_date).toLocaleDateString()}
                  </Badge>
                )}
              </div>

              {/* Acknowledgment requirement alert */}
              {selectedPolicy.requires_acknowledgment && !acknowledgments[selectedPolicy.id]?.acknowledged && (
                <Alert className="bg-orange-50 border-orange-200">
                  <Clock className="h-4 w-4 text-orange-600" />
                  <AlertDescription className="text-orange-800">
                    This policy requires your acknowledgment. Please read it carefully and click the &quot;Acknowledge&quot; button below.
                  </AlertDescription>
                </Alert>
              )}

              {acknowledgments[selectedPolicy.id]?.acknowledged && (
                <Alert className="bg-green-50 border-green-200">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-800">
                    You acknowledged this policy on {new Date(acknowledgments[selectedPolicy.id].acknowledged_at).toLocaleDateString()}
                  </AlertDescription>
                </Alert>
              )}

              {/* Policy content */}
              <div className="prose max-w-none">
                <div 
                  className="text-gray-700 leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: selectedPolicy.content }}
                />
              </div>

              {/* Tags */}
              {selectedPolicy.tags?.length > 0 && (
                <div className="flex gap-2 pt-4 border-t">
                  {selectedPolicy.tags.map((tag, idx) => (
                    <Badge key={idx} variant="outline" className="text-xs">{tag}</Badge>
                  ))}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPolicyModal(false)}>
              Close
            </Button>
            {selectedPolicy?.requires_acknowledgment && !acknowledgments[selectedPolicy?.id]?.acknowledged && (
              <Button 
                className="bg-green-600 hover:bg-green-700"
                onClick={() => handleAcknowledge(selectedPolicy.id)}
                disabled={acknowledging}
                data-testid="acknowledge-policy-button"
              >
                {acknowledging && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                <Check className="h-4 w-4 mr-2" />
                I Acknowledge This Policy
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Create/Edit Policy Modal */}
      {isAdmin && (
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="max-w-2xl" data-testid="create-policy-modal">
            <DialogHeader>
              <DialogTitle>{editingPolicy ? 'Edit Policy' : 'Create New Policy'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Title *</label>
                <Input
                  placeholder="Policy title"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  data-testid="policy-title-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Category</label>
                <Select value={formData.category} onValueChange={(v) => setFormData(prev => ({ ...prev, category: v }))}>
                  <SelectTrigger data-testid="policy-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => (
                      <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Summary</label>
                <Input
                  placeholder="Brief summary (optional)"
                  value={formData.summary}
                  onChange={(e) => setFormData(prev => ({ ...prev, summary: e.target.value }))}
                  data-testid="policy-summary-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Content *</label>
                <Textarea
                  placeholder="Policy content..."
                  value={formData.content}
                  onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                  className="min-h-[200px]"
                  data-testid="policy-content-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Tags (comma separated)</label>
                <Input
                  placeholder="e.g. leave, annual, hr"
                  value={formData.tags}
                  onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                  data-testid="policy-tags-input"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="requires_ack"
                  checked={formData.requires_acknowledgment}
                  onChange={(e) => setFormData(prev => ({ ...prev, requires_acknowledgment: e.target.checked }))}
                  className="rounded border-gray-300"
                  data-testid="policy-requires-ack-checkbox"
                />
                <label htmlFor="requires_ack" className="text-sm text-gray-700">Require employee acknowledgment</label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
              <Button onClick={handleSavePolicy} disabled={saving} className="bg-[#0F2E6E] hover:bg-[#0a1f4d]" data-testid="save-policy-btn">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingPolicy ? 'Update Policy' : 'Create Policy'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default CompanyPolicies;
